import { useCallback, useEffect, useState } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Download,
  Loader2,
  RefreshCcw,
  Search,
  Users,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Phone,
  Mail,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const PAGE_SIZE = 50;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  synced: { bg: "bg-emerald-50", text: "text-emerald-700", icon: CheckCircle2, label: "Sincronizado" },
  failed: { bg: "bg-red-50", text: "text-red-700", icon: AlertTriangle, label: "Falló" },
  pending: { bg: "bg-amber-50", text: "text-amber-700", icon: Clock, label: "Pendiente" },
};

function StatusPill({ status, message }) {
  const cfg = STATUS_PILL[status] || STATUS_PILL.pending;
  const Icon = cfg.icon;
  return (
    <span
      title={message || ""}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${cfg.bg} ${cfg.text}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, accent = "bg-neutral-100 text-neutral-700" }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-4 flex items-center gap-3">
      <span className={`inline-flex w-10 h-10 rounded-xl items-center justify-center ${accent}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-neutral-400">{label}</p>
        <p className="font-heading font-bold text-xl text-[#111111] tabular-nums">
          {value.toLocaleString()}
        </p>
      </div>
    </div>
  );
}

export default function AdminContactsTab() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [userType, setUserType] = useState("any");
  const [syncStatus, setSyncStatus] = useState("any");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [resyncingAll, setResyncingAll] = useState(false);
  const [resyncingUser, setResyncingUser] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/contacts/stats");
      setStats(data);
    } catch (e) {
      // silent — non-blocking
    }
  }, []);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (q) params.q = q;
      if (userType !== "any") params.user_type = userType;
      if (syncStatus !== "any") params.sync_status = syncStatus;
      const { data } = await api.get("/admin/contacts", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Error al cargar contactos");
    } finally {
      setLoading(false);
    }
  }, [page, q, userType, syncStatus]);

  useEffect(() => {
    fetchStats();
    fetchPage();
  }, [fetchStats, fetchPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPage();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/contacts/export`, { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rivones_contacts.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar el CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleResyncOne = async (userId) => {
    setResyncingUser(userId);
    try {
      const { data } = await api.post(`/admin/contacts/${userId}/resync`);
      const r = data?.result || {};
      const okR = r.resend?.status === "synced";
      const okS = r.sendgrid?.status === "synced";
      if (okR && okS) toast.success("Contacto sincronizado en ambos proveedores");
      else if (okR) toast.success("Sincronizado en Resend (SendGrid falló)");
      else if (okS) toast.success("Sincronizado en SendGrid (Resend falló)");
      else toast.error("Sincronización falló en ambos proveedores");
      await fetchPage();
      await fetchStats();
    } catch (e) {
      toast.error("Error al reintentar");
    } finally {
      setResyncingUser(null);
    }
  };

  const handleResyncAllFailed = async () => {
    setResyncingAll(true);
    try {
      const { data } = await api.post("/admin/contacts/resync-failed");
      toast.success(
        `Reintentados: ${data.attempted}. Éxitos: ${data.successes}. Fallos: ${data.failures}`,
      );
      await fetchPage();
      await fetchStats();
    } catch (e) {
      toast.error("Error en el reintento masivo");
    } finally {
      setResyncingAll(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="admin-contacts-tab">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-terracotta to-amber-500 items-center justify-center text-white">
              <Users className="w-4 h-4" />
            </span>
            <h2 className="font-heading font-bold text-xl text-agave">User Contacts</h2>
          </div>
          <p className="text-sm text-neutral-500 mt-1">
            Sincronización de usuarios con Resend y Twilio SendGrid.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleResyncAllFailed}
            disabled={resyncingAll}
            data-testid="contacts-resync-failed-btn"
          >
            {resyncingAll ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCcw className="w-4 h-4 mr-2" />
            )}
            Reintentar fallidos
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting || total === 0}
            data-testid="contacts-export-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={Users} label="Total" value={stats.total_users} accent="bg-neutral-100 text-neutral-700" />
          <StatCard icon={Users} label="Renters" value={stats.renters} accent="bg-blue-50 text-blue-600" />
          <StatCard icon={Users} label="Hosts" value={stats.hosts} accent="bg-violet-50 text-violet-600" />
          <StatCard icon={Mail} label="Consentimiento Email" value={stats.email_consent} accent="bg-amber-50 text-amber-600" />
          <StatCard icon={Phone} label="Consentimiento SMS" value={stats.sms_consent} accent="bg-emerald-50 text-emerald-600" />
          <StatCard icon={Phone} label="Con teléfono" value={stats.with_phone} accent="bg-cyan-50 text-cyan-600" />
        </div>
      )}

      {/* Sync provider status row */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white border border-neutral-200 rounded-2xl p-4">
            <p className="text-sm font-semibold text-[#111111] mb-2">Resend</p>
            <div className="flex flex-wrap gap-2">
              <StatusPill status="synced" /> <span className="font-mono text-sm">{stats.resend.synced}</span>
              <StatusPill status="failed" /> <span className="font-mono text-sm">{stats.resend.failed}</span>
              <StatusPill status="pending" /> <span className="font-mono text-sm">{stats.resend.pending}</span>
            </div>
          </div>
          <div className="bg-white border border-neutral-200 rounded-2xl p-4">
            <p className="text-sm font-semibold text-[#111111] mb-2">Twilio SendGrid</p>
            <div className="flex flex-wrap gap-2">
              <StatusPill status="synced" /> <span className="font-mono text-sm">{stats.sendgrid.synced}</span>
              <StatusPill status="failed" /> <span className="font-mono text-sm">{stats.sendgrid.failed}</span>
              <StatusPill status="pending" /> <span className="font-mono text-sm">{stats.sendgrid.pending}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            type="search"
            placeholder="Buscar por correo o nombre…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="contacts-search-input"
          />
        </div>
        <Select value={userType} onValueChange={(v) => { setUserType(v); setPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="contacts-usertype-filter">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Todos los tipos</SelectItem>
            <SelectItem value="renter">Renter</SelectItem>
            <SelectItem value="host">Host</SelectItem>
            <SelectItem value="both">Both</SelectItem>
          </SelectContent>
        </Select>
        <Select value={syncStatus} onValueChange={(v) => { setSyncStatus(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]" data-testid="contacts-syncstatus-filter">
            <SelectValue placeholder="Estado de sync" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Cualquier estado</SelectItem>
            <SelectItem value="synced">Sincronizado</SelectItem>
            <SelectItem value="failed">Falló</SelectItem>
            <SelectItem value="pending">Pendiente</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" variant="secondary" data-testid="contacts-search-btn">
          Buscar
        </Button>
      </form>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-neutral-500 text-sm">
            No se encontraron contactos con esos filtros.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead className="hidden md:table-cell">Teléfono</TableHead>
                  <TableHead className="hidden sm:table-cell">Tipo</TableHead>
                  <TableHead className="hidden lg:table-cell">Consent.</TableHead>
                  <TableHead>Resend</TableHead>
                  <TableHead>SendGrid</TableHead>
                  <TableHead className="hidden md:table-cell">Registro</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.user_id} data-testid={`contacts-row-${row.user_id}`}>
                    <TableCell>
                      <p className="font-medium text-sm text-[#111111]">
                        {row.first_name || ""} {row.last_name || ""}
                      </p>
                      <p className="text-xs text-neutral-500">{row.email}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm font-mono text-neutral-600">
                      {row.phone_e164 || "—"}
                      {row.phone_e164 && row.phone_verified && (
                        <span className="ml-1 inline-flex items-center text-emerald-600" title="Verificado">
                          <CheckCircle2 className="w-3 h-3" />
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="text-xs uppercase tracking-wider text-neutral-500">
                        {row.user_type}
                      </span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {row.marketing_consent ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium">
                          <CheckCircle2 className="w-3 h-3" /> sí
                        </span>
                      ) : (
                        <span className="text-neutral-400 text-xs">no</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusPill status={row.resend_status} message={row.resend_message} />
                    </TableCell>
                    <TableCell>
                      <StatusPill status={row.sendgrid_status} message={row.sendgrid_message} />
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-neutral-500">
                      {row.created_at ? format(new Date(row.created_at), "dd MMM yy") : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={resyncingUser === row.user_id}
                        onClick={() => handleResyncOne(row.user_id)}
                        data-testid={`contacts-resync-${row.user_id}`}
                      >
                        {resyncingUser === row.user_id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <RefreshCcw className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-neutral-500">
            {total.toLocaleString()} contactos · Página {page} de {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
